import { PITCH_CONFIDENCE_MIN } from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';
import {
  RANGE_PERCENTILE,
  analyzeVibrato,
  breathSegments,
  mean,
  percentile,
  pitchRegisters,
  runSeconds,
  toPitchSeries,
  voicedRuns,
} from '@muzly/voice-engine';
import type {
  BreathSegment,
  Clip,
  ClipSet,
  PitchRegister,
  PitchSample,
  PitchSeries,
  RangeInfo,
  Vibrato,
} from '@muzly/voice-engine';

/**
 * framesToClips — 네이티브 PitchTracker 프레임을 voice-engine `ClipSet`으로 바꾸는
 * 순수 변환 (네이티브 캡처 경로의 유일한 seam).
 *
 * 진단은 테스트마다 따로 녹음한다(`/a/` 길게 · 저음 · 고음 · 노래) → **세그먼트 하나가
 * clip 하나**로 직결되고, 자를 일이 없다. 예전에는 단일 녹음을 사후에 쪼갰는데
 * (`splitCapture`: 끝 1.3초=지속음, 앞=상승 스윕) 그 가정은 두 군데서 틀렸다 —
 * MPT를 1.3초 창에서 재게 되고, 상승 스윕을 반으로 갈라 low/high라 부르면 사용자가
 * 실제로 낸 최저·최고음이 아니다.
 *
 * 테스트는 여러 번 시도할 수 있어 세그먼트마다 **시도 목록**이 들어온다. 합치는 규칙은
 * 지표별로 다르다 — 아래 mergeSustain·bestRange 참고.
 *
 * 네이티브는 raw PCM을 JS로 넘기지 않으므로(GOAL.md 불변식) 프레임에 스펙트럼 배열이
 * 없다 → `Clip`의 스펙트럼 칸(hnrs·centroids·singersFormant)은 영영 빈 배열이다.
 * 그 축을 되살리는 건 배열이 아니라 **스칼라**다: 네이티브가 창마다 스펙트럼을 재서
 * 요약 숫자만 프레임에 싣는다(ABI v4). 그것도 못 실어 오는 회차는 미측정으로 표시된다.
 *
 * 그래서 `Clip`에 담을 수 없는 것들이 **`frameStats`라는 별도 봉투로** 나간다 — 네이티브가
 * 실어 보내는 파생 스칼라(`hnrDb`·`resonanceRatio`와 v4 공명 넷 — f0frame-contract §1)도, f0 시계열에서
 * 계산하는 떨림(`vibrato`)도 거기 있다. `Clip.hnrs`에 넣지 않는 이유: 그 칸은
 * landing이 스펙트럼에서 뽑는 0..10 무차원 프록시(`spectral.ts`)를 담는 자리이고,
 * 네이티브 값은 실제 dB다. 눈금이 다른 값을 같은 칸에 넣으면 그걸 읽는 엔진 산식
 * (`estimateVocalAge`의 `hnrMean*5`, `vocalCharacter`의 `hnrMean < 3.5`)이 조용히
 * 포화된다 — 지금은 web이 그 파생값을 안 쓰지만, 쓰는 날 화면이 거짓말을 한다.
 *
 * React·오디오 IO 의존 없음 — vitest 대상.
 */

/**
 * 유성 & 신뢰도 충분 & f0 유효 — 음정·MPT가 공유하는 프레임 판정.
 * 문턱은 엔진 게이트(config.confidenceThreshold)와 같은 선이고, 값의 출처는
 * `@muzly/shared`의 PITCH_CONFIDENCE_MIN 하나뿐이다(여기서 별칭을 다시 만들지 않는다).
 */
export const isConfidentVoiced = (f: NativeF0Frame): boolean =>
  f.voiced && f.f0Hz != null && f.confidence >= PITCH_CONFIDENCE_MIN;

/** voiced & 신뢰도 충분 & f0 유효 프레임만 → f0(Hz) 리스트(시간순). */
const voicedF0 = (frames: NativeF0Frame[]): number[] =>
  frames.filter(isConfidentVoiced).map((f) => f.f0Hz as number);

/**
 * 발성이 끊기지 않고 이어졌다고 볼 수 있는 무성 공백의 상한(ms).
 *
 * 자음·순간 드롭은 발성이 **이어지는 중**이고, 그보다 긴 공백은 한 번 끝난 것이다.
 *
 * **250 (2026-10-15).** 예전 500은 숨을 끊었다 다시 낸 두 발성을 하나로 이어 MPT를 늘렸다.
 * 연구 데이터 지속음 57테이크 재생(`tools/pitch-replay`, 진단 게이트 -60; 정답 = 큰 소리 구간에서
 * pyin·Praat 중 하나가 유성인 가장 긴 구간, 100ms 이하 구멍은 이음 — 정의는 `refs.py` `run_s`)의
 * 평균 절대 오차: 500 → 1.41초, 250 → 1.13초, 150 → 1.07초,
 * 100 → 1.24초. 150은 250보다 0.06초 나을 뿐이라 덜 공격적인 250에 뒀다.
 */
const MAX_VOICED_GAP_MS = 250;

/**
 * 최대발성지속시간(MPT) = **한 번에 이어진 가장 긴 발성**.
 *
 * 완료 버튼을 누른 시각이 아니다: 숨이 끊긴 뒤 버튼까지의 반응 시간은 사람마다 수 초씩
 * 다르고, 그게 그대로 MPT에 더해지면 "숨이 긴 사람"이 아니라 "손이 빠른 사람"을 재게 된다.
 *
 * **끝점 두 개만 보면 안 된다**(2026-08-05 실기기): 첫 유성부터 마지막 유성까지를 그냥
 * 재면 사이의 침묵이 전부 발성으로 들어간다 — 경과 1분 45초 세션에서 MPT가 105.7초로
 * 찍혔다. 재는 것이 "한 숨에 얼마나 버티나"가 아니라 "화면을 얼마나 오래 켜뒀나"가 된다.
 * 그래서 공백에 상한(`MAX_VOICED_GAP_MS`)을 두고, 그보다 긴 공백에서 끊는다. 짧은 끊김이
 * 구간 안에 남아야 한다는 원래 의도는 그대로다 — 상한이 없었을 뿐이다.
 *
 * 여러 번 냈으면 **가장 긴 것**을 쓴다. MPT는 최댓값을 재는 시험이라 시도가 여럿이면
 * 잘된 쪽이 답이고, 그건 시도 간 병합 규칙(`framesToClips`)과도 같은 방향이다.
 *
 * 유성 프레임이 2개 미만이면 잴 구간이 없다 → 0.
 */
export const voicedSpanSec = (frames: NativeF0Frame[]): number =>
  // 끊는 규칙은 엔진이 갖고(`voicedRuns`), 여기가 정하는 건 **공백의 상한**뿐이다 —
  // 같은 "쉼에서 끊는다"를 두 벌로 적으면 한쪽만 고쳐진 채 남는다.
  voicedRuns(voicedSamples(frames), MAX_VOICED_GAP_MS).reduce(
    (longest, run) => Math.max(longest, runSeconds(run)),
    0,
  );

/** 세그먼트 = 테스트 하나의 녹음. */
export type SegmentId = 'sustain' | 'low' | 'high' | 'song';

/**
 * 음역 끝을 재는 세그먼트. `Exclude<SegmentId, 'sustain'>`가 **아니다** — 노래
 * 따라부르기는 sustain도 음역도 아닌 세 번째 종류라서, 빼기로 정의하면 세그먼트가 늘 때마다
 * 퍼센타일 대상이 조용히 넓어진다(`RANGE_PERCENTILE`이 곧바로 거짓이 된다).
 */
export type RangeSegmentId = 'low' | 'high';

/**
 * 테스트별 **시도 목록**(시간순, 재시도하면 뒤에 쌓인다). `sustain`만 필수 —
 * 리포트가 성립하는 최소 단위라서다.
 *
 * `song`은 여기 실리지만 `ClipSet`으로는 안 나간다: 노래는 멜로디 대비 채점(`songScoring`)
 * 대상이라 clip(퍼센타일·MPT)으로 환산할 게 없다. 프레임을 통째로 보는 소비처가 따로 있다.
 */
export type CaptureAttempts = { sustain: NativeF0Frame[][] } & Partial<
  Record<RangeSegmentId | 'song', NativeF0Frame[][]>
>;

/**
 * 세그먼트별 시도(전부 optional) → `CaptureAttempts`(지속음이 필수인 모양).
 *
 * 채우는 자리를 **하나로** 두는 이유: 클립·파생 스칼라와 음역 구간이 같은 프레임 묶음을
 * 봐야 한다. 호출부마다 따로 채우면 "어느 시도를 세느냐"가 두 벌이 되고, 한쪽만 고쳐진 채
 * 남아도 화면은 두 값을 나란히 보여준다.
 */
export const captureAttempts = (
  attempts: Partial<Record<SegmentId, NativeF0Frame[][]>>,
): CaptureAttempts => ({ ...attempts, sustain: attempts.sustain ?? [] });

/**
 * 프레임이 실어 오는 파생 스칼라의 이름들. `FrameStats`와 `NativeF0Frame`이 **같은 이름**을
 * 쓰기 때문에 목록 하나로 여섯을 다 집계할 수 있다 — 스칼라가 하나 늘 때 고칠 자리도 하나다.
 *
 * 계약상 이 값들은 voiced일 때만 의미가 있다(f0frame-contract §1) — 숨소리 구간까지 세면
 * 발성 품질이 아니라 방 소음이 섞인다. 값을 실어 오지 않는 프레임(구 네이티브 바이너리·mock)은
 * 그냥 빠지고, 하나도 없으면 그 필드가 통째로 없다(0이 아니다).
 */
const SCALAR_KEYS = [
  'hnrDb',
  'resonanceRatio',
  // v4 스칼라 넷. 구 네이티브는 안 실어 온다.
  'resonanceLevelDb',
  'resonanceCenterHz',
  'brightnessHz',
  'bandwidthHz',
] as const;

/**
 * 지속음 세그먼트에서 나오는 **클립 밖 파생값**. `Clip`은 landing·admin과 공유하는
 * 모양이라 여기 담을 수 없는 것들이 모이는 자리다(모듈 주석).
 *
 * **없는 것과 0을 섞지 않는다** — 필드가 통째로 빠지는 조합(구 네이티브 + 신 JS)에서 0을
 * 내면 화면이 "울림 0점"이라 말한다. 소비처는 undefined를 "이번엔 못 쟀어요"로 그린다.
 */
export type FrameStats = {
  /** harmonics-to-noise 평균(dB). */
  hnrDb?: number;
  /** 고역 대역 에너지비 프록시 평균(0..1). */
  resonanceRatio?: number;
  /**
   * 공명 세기 평균(dB). **프레임 dB의 평균이지 `resonanceRatio` 평균의 dB가 아니다** —
   * 네이티브가 프레임마다 이미 잰 값을 그대로 평균한다. 여기서 비율로 다시 만들면
   * 바닥값(-40dB) 같은 네이티브 소유 상수를 web이 베껴 들고 있어야 한다.
   */
  resonanceLevelDb?: number;
  /** 울림 대역 무게중심 평균(Hz). 대역 경계는 네이티브가 소유한다. */
  resonanceCenterHz?: number;
  /** 스펙트럼 무게중심(밝기) 평균(Hz). */
  brightnessHz?: number;
  /** 무게중심 둘레의 퍼짐(대역폭) 평균(Hz). */
  bandwidthHz?: number;
  /**
   * 음의 떨림 — 속도·폭·규칙성. 위 둘과 달리 프레임이 실어 오는 값이 **아니고**
   * f0 시계열에서 계산된다(`analyzeVibrato`). 그래도 같은 봉투에 담는 이유는 성질이
   * 같아서다: 지속음에서만 나오고, 마지막 시도 기준이고, 못 재면 필드가 없다.
   */
  vibrato?: Vibrato;
  /**
   * 지속음을 시작·중간·후반으로 갈라 잰 흔들림 점수 — 경계는 테이크 길이의 비율이라
   * 그 회차의 실제 초(`fromSec`·`toSec`)가 같이 실린다. 표본이 모자란 구간은 `score`가
   * **없다**(0이 아니다 — `breathSegments` 주석). 셋 중 하나도 못 재면 필드 자체가 없다.
   */
  breath?: BreathSegment[];
  /**
   * 음정 시계열 — 결과 화면의 호흡·떨림 차트가 그리는 곡선(`toPitchSeries`). 비브라토와
   * 같은 표본에서 같은 옥타브 되접기로 나오므로 차트와 그 옆 속도·폭이 같은 테이크를 말한다.
   * 레코드에만 싣고 서버로는 안 보낸다(plan Decisions "시계열 저장 위치").
   */
  pitchSeries?: PitchSeries;
};

/**
 * 유성 프레임 → **시간축이 붙은** 표본. `Clip.pitches`가 못 하는 일이다: 그쪽은 f0만
 * 모은 자루라 "초당 몇 번"을 물을 수 없고, 무성 구간이 이미 지워져 숨 자리가 어디였는지도
 * 남지 않는다. 여기서는 걸러진 프레임의 `timestampMs`를 그대로 들고 가, 공백은 공백으로
 * 남긴다 — 그 공백에서 끊는 판단은 엔진이 한다.
 */
export const voicedSamples = (frames: NativeF0Frame[]): PitchSample[] =>
  frames
    .filter(isConfidentVoiced)
    .map((f) => ({ tMs: f.timestampMs, f0Hz: f.f0Hz as number }));

/**
 * 이보다 긴 **프레임** 간격은 소리를 담고 있던 시간으로 안 센다. 엔진 프레임은 수십 ms
 * 간격이라(hop) 1초는 정상 흐름에서 절대 안 나오는 값이다 — 무성 구간에도 프레임은 오므로
 * 이 간격은 일시 중단이거나 스트림이 끊겼던 구간이다(유성 표본 사이의 간격과 다르다 — 그건
 * 침묵도 포함한다).
 */
export const CAPTURE_GAP_MS = 1000;

/**
 * 일시 중단 하나가 담은 시간에서 차지하는 자리(ms). 0이면 앞뒤 유성 구간이 한 구간으로
 * 붙어 비브라토가 멈춤을 가로질러 주기를 세고(한 구간 = 60ms 틈까지), 곡선에도 빈 칸이 안
 * 남는다. 100ms면 곡선(50ms 칸)에 빈 칸 하나가 남고 비브라토 구간은 거기서 끊긴다.
 */
const PAUSE_STEP_MS = 100;

/**
 * `voicedSamples`와 같되 시각이 **담은 시간**이다 — 일시 중단(`CAPTURE_GAP_MS`보다 긴 프레임
 * 간격)을 `PAUSE_STEP_MS`로 접는다. 지속음 파생값(비브라토·호흡 구간·음정 곡선)이 이걸
 * 함께 써야 곡선의 골과 구간 점이 같은 자리를 가리킨다.
 *
 * MPT(`voicedSpanSec`)는 이걸 쓰지 않는다: 멈춤 앞뒤를 한 번의 발성으로 이으면 안 된다.
 */
const capturedVoicedSamples = (frames: NativeF0Frame[]): PitchSample[] => {
  const samples: PitchSample[] = [];
  let shift = 0;
  frames.forEach((frame, i) => {
    const gap = i === 0 ? 0 : frame.timestampMs - frames[i - 1].timestampMs;
    if (gap > CAPTURE_GAP_MS) {
      shift += gap - PAUSE_STEP_MS;
    }
    if (isConfidentVoiced(frame)) {
      samples.push({
        tMs: frame.timestampMs - shift,
        f0Hz: frame.f0Hz as number,
      });
    }
  });
  return samples;
};

/**
 * 호흡 구간 — **하나라도 점수가 붙었을 때만** 싣는다.
 *
 * `breathSegments`는 이제 셋을 항상 돌려준다(점수 없는 구간은 줄만). 그 줄들은 화면이
 * 라벨을 그리는 데 쓰이지만, 셋 다 점수가 없는 회차는 그릴 값이 하나도 없는 것과 같아서
 * 필드를 두면 결과 탭이 빈 채로 선다 — 이 봉투의 "없으면 필드가 없다" 규약대로 뺀다.
 */
const scoredBreath = (samples: PitchSample[]): BreathSegment[] | undefined => {
  const segments = breathSegments(samples);
  return segments.some((s) => s.score !== undefined) ? segments : undefined;
};

/** 프레임열 → 파생값. 필드끼리 서로 독립이다(하나만 나와도 된다). */
export const frameStats = (frames: NativeF0Frame[]): FrameStats => {
  // 유성 걸러내기를 스칼라마다 되풀이하지 않는다 — 여섯이 전부 같은 프레임을 본다.
  const voiced = frames.filter(isConfidentVoiced);
  const samples = capturedVoicedSamples(frames);
  const scalars: Partial<Record<(typeof SCALAR_KEYS)[number], number>> = {};
  for (const key of SCALAR_KEYS) {
    const values = voiced
      .map((frame) => frame[key])
      .filter((v): v is number => v != null);
    if (values.length > 0) {
      scalars[key] = mean(values);
    }
  }
  return {
    ...scalars,
    // null·빈 배열은 "못 쟀다"인데 이 봉투의 규약은 "없으면 필드가 없다" — 여기서 맞춘다.
    vibrato: analyzeVibrato(samples) ?? undefined,
    breath: scoredBreath(samples),
    pitchSeries: toPitchSeries(samples) ?? undefined,
  };
};

export const framesToClip = (frames: NativeF0Frame[]): Clip => ({
  pitches: voicedF0(frames),
  // 네이티브 HNR은 여기 안 담는다 — 눈금이 다르다(위 모듈 주석). frameStats로 나간다.
  hnrs: [],
  centroids: [],
  singersFormant: [],
  voicedSec: voicedSpanSec(frames),
});

/**
 * 음역 끝으로 인정하는 "머문 음" 하나의 조건. 값의 출처는 2026-10-11 실기기 재생
 * (prod·dev 업로드 테이크, 저음 95·고음 105개를 CREPE·pYIN 합의 정답과 대조) — 아래
 * `heldRangeF0` 주석의 표.
 */
/**
 * 한 음으로 이어 보는 최대 변화(반음) — 직전 프레임과도, 그 음의 지금까지 중앙값과도.
 * 중앙값 쪽이 없으면 프레임당 1반음 미만으로 천천히 올라가는 글라이드 전체가 한 "음"이 되어
 * 머문 적 없는 꼭대기가 끝음이 된다(같은 재생에서 고음 중앙 오차 0.29 → 0.09).
 */
const HELD_STEP_SEMITONES = 1;
/** 이보다 긴 무성 공백은 음을 끊는다(ms) — 엔진 프레임 두 개 남짓. */
const HELD_MAX_GAP_MS = 60;
/** 이보다 짧게 머문 음은 음으로 치지 않는다(ms, 첫 프레임~끝 프레임). ≈ 8프레임. */
const HELD_MIN_RUN_MS = 140;
/**
 * 끝음으로 채택하려면 그 높이(±`HELD_STEP_SEMITONES`)에 머문 시간의 **합**이 이만큼
 * 있어야 한다(ms) — 한 번 길게든 여러 번 나눠서든. 음성의학 음역 측정(VRP)의 "지속·재현"
 * 요건을 1회 진단 길이에 맞춘 값이다.
 *
 * **저음은 320.** 가장 낮은 음은 숨이 빠지며 조용하고 짧게 끝나는 일이 많아 480이면
 * 그 음을 놓치고 한두 음 위가 끝음이 된다. 연구 데이터 재생(`tools/pitch-replay`,
 * 2026-10-15, 진단 게이트 -60): 저음 끝 100c 이내 480 → 42/55, 400 → 43, 320 → 45,
 * 240 → 46. 240은 고음 쪽에서 과대를 늘렸던 값(아래 `heldRangeF0` 표)이라 320에 뒀다.
 * 고음은 그 과대("C#까지 냈는데 A5")가 사용자 신고였으므로 480 그대로다.
 */
const HELD_SUPPORT_MS: Record<RangeSegmentId, number> = { low: 320, high: 480 };
/**
 * 저음 시도의 끝음이 이보다 높으면(Hz) 이상치로 본다 — 저음 시도의 가장 낮은 음이 고음역일
 * 수는 없다. 연구 데이터 저음 테이크 #229(`tools/pitch-replay` 색인)가 저음 715Hz로 찍혔다(진단 게이트 -45). 그때는
 * 머문 음 대신 근거 프레임 전부의 퍼센타일로 물러선다.
 */
const LOW_END_MAX_HZ = 500;
/** raw f0 가 표시용 스무딩 값과 이만큼 넘게 갈리면 그 프레임은 오검출 후보로 본다(반음). */
const HELD_SMOOTHED_AGREE_SEMITONES = 1;

const toMidi = (hz: number): number => 69 + 12 * Math.log2(hz / 440);

/**
 * 음역 근거로 쓸 수 있는 프레임 — 유성·신뢰도에 더해, 엔진의 표시 스무딩과 어긋나지 않을 것.
 *
 * 스무딩이 도는 테이크에서 스무딩 값이 **없는** 프레임은 엔진이 화면에서 걷어낸 프레임이다
 * (버스트·옥타브 이탈) — 근거가 아니다. 테이크 전체에 스무딩 값이 하나도 없을 때(브라우저
 * mock·스무딩 off)만 raw 로 판단한다. `displayMidi`의 `smoothingActive`와 같은 구분이다.
 */
const isRangeEvidence = (
  f: NativeF0Frame,
  smoothingActive: boolean,
): boolean => {
  if (!isConfidentVoiced(f)) {
    return false;
  }
  if (f.smoothedF0Hz == null) {
    return !smoothingActive;
  }
  return (
    Math.abs(toMidi(f.f0Hz as number) - toMidi(f.smoothedF0Hz)) <
    HELD_SMOOTHED_AGREE_SEMITONES
  );
};

/** 머문 음 하나 — 그 프레임들의 f0와 중앙 높이(midi), 머문 시간. */
interface HeldNote {
  f0Hz: number[];
  midi: number;
  spanMs: number;
}

const median = (values: number[]): number => {
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** 시간순 프레임 → 머문 음들. 큰 도약·긴 공백·근거 아닌 프레임에서 끊는다. */
const heldNotes = (frames: NativeF0Frame[]): HeldNote[] => {
  const notes: HeldNote[] = [];
  const smoothingActive = frames.some((f) => f.smoothedF0Hz != null);
  let run: NativeF0Frame[] = [];
  let runMidi: number[] = [];
  const close = (): void => {
    if (run.length > 1) {
      const spanMs = run[run.length - 1].timestampMs - run[0].timestampMs;
      if (spanMs >= HELD_MIN_RUN_MS) {
        const f0Hz = run.map((f) => f.f0Hz as number);
        notes.push({ f0Hz, midi: median(f0Hz.map(toMidi)), spanMs });
      }
    }
    run = [];
    runMidi = [];
  };
  for (const frame of frames) {
    if (!isRangeEvidence(frame, smoothingActive)) {
      const last = run[run.length - 1];
      if (last && frame.timestampMs - last.timestampMs > HELD_MAX_GAP_MS) {
        close();
      }
      continue;
    }
    const last = run[run.length - 1];
    const midi = toMidi(frame.f0Hz as number);
    if (
      last &&
      (frame.timestampMs - last.timestampMs > HELD_MAX_GAP_MS ||
        Math.abs(midi - runMidi[runMidi.length - 1]) > HELD_STEP_SEMITONES ||
        Math.abs(midi - median(runMidi)) > HELD_STEP_SEMITONES)
    ) {
      close();
    }
    run.push(frame);
    runMidi.push(midi);
  }
  close();
  return notes;
};

/**
 * 저음·고음 시도의 **끝음을 이루는 프레임들의 f0** — 끝음이 없으면 빈 배열.
 *
 * 예전에는 신뢰도만 넘은 프레임 전부의 퍼센타일(저음 12·고음 88)이 끝음이었다. 그러면
 * 순간 오검출이 그대로 결과가 된다: 큰 소리로 음을 여는 순간 엔진이 기본음 대신 강한
 * 고배음(1.1kHz 위 공명, 클리핑된 2.7kHz 대역)의 주기 배수를 잡아 한두 프레임씩
 * A5(≈880Hz) 근처를 읽는데, 그런 프레임이 12%만 넘으면 퍼센타일이 그 값을 고른다
 * (2026-10-07 사용자 테스트 "C#까지 냈는데 A5", 그 값으로 테너 판정). 프라이로 떨어진
 * 61Hz 근처 발성도 같은 길로 600~700Hz가 된다(하한 65Hz 밖이라 엔진이 배음을 읽는다).
 *
 * 그래서 끝음은 **머문 음**에서 고른다: 한 높이에 `HELD_MIN_RUN_MS` 이상 이어진 음들 중,
 * 같은 높이에 머문 시간의 합이 `HELD_SUPPORT_MS`를 넘는 가장 바깥 음. 그 음의 프레임을
 * 돌려주므로 `rangeHz`의 퍼센타일은 그 음 안에서만 돈다.
 *
 * 실기기 재생(고음 105·저음 102 테이크). 정답 = CREPE·pYIN이 1반음 안에서 합의한 프레임으로
 * 0.2초 창 중앙값의 최댓값(고음)·최솟값(저음) — 이 구현과 독립인 기준이다. 오차 단위는 반음:
 *
 * | | 과대(≥3) 고음/저음 | 과소(≥3) 고음/저음 | 중앙 오차 고음/저음 | 끝음 없음 고음/저음 |
 * |---|---|---|---|---|
 * | 퍼센타일(이전) | 3 / 8 | 13 / 12 | 0.66 / 0.74 | 0 / 6 |
 * | 머문 음(지금, `rangeFramesToClip`) | 1 / 2 | 13 / 16 | 0.09 / 0.39 | 1 / 6 |
 *
 * 과대 건수는 적어도 그 하나하나가 사용자 신고다("C#까지 냈는데 A5"). 머문 시간 합 240ms로
 * 낮추면 고음 과대가 5로, 600ms로 올리면 머문 음 없는 시도가 늘어 480ms에 뒀다. 저음은
 * 320ms다(이유는 `HELD_SUPPORT_MS` 주석).
 * 근거가 모자라면 빈 배열이다 — `rangeFramesToClip`이 그때만 퍼센타일로 물러선다.
 */
export const heldRangeF0 = (
  frames: NativeF0Frame[],
  id: RangeSegmentId,
): number[] => {
  const notes = heldNotes(frames);
  const outward = notes
    .slice()
    .sort((a, b) => (id === 'high' ? b.midi - a.midi : a.midi - b.midi));
  for (const note of outward) {
    const near = notes.filter(
      (n) => Math.abs(n.midi - note.midi) <= HELD_STEP_SEMITONES,
    );
    const supportMs = near.reduce((sum, n) => sum + n.spanMs, 0);
    if (supportMs >= HELD_SUPPORT_MS[id]) {
      // 저음 끝이 고음역이면 끝음이 아니라 오검출이다 — 머문 음 없음으로 친다. 여기서
      // 거르므로 `rangeFramesToClip`과 `bestRange`가 같은 판정을 쓴다.
      return id === 'low' && median(note.f0Hz) >= LOW_END_MAX_HZ
        ? []
        : note.f0Hz;
    }
  }
  return [];
};

/**
 * 저음·고음 시도 하나 → clip. `pitches`만 끝음의 프레임이다 — 그 clip을 읽는 모든
 * 자리(`rangeHz`, `buildReport`의 퍼센타일, 시도 요약·경고)가 같은 끝음을 말하게.
 *
 * 머문 음이 없으면(짧게 훑기만 한 시도) 근거 프레임 전부를 돌려 예전처럼 퍼센타일이 고르게
 * 한다. 빈 배열로 두면 저음 시도 null이 6건 → 12건으로 늘어 "음역을 재지 못했어요"가 그만큼
 * 더 뜬다. 근거 프레임은 스무딩이 걷어낸 버스트를 이미 뺐으므로 과대는 늘지 않았다(같은 재생).
 */
export const rangeFramesToClip = (
  frames: NativeF0Frame[],
  id: RangeSegmentId,
): Clip => {
  const held = heldRangeF0(frames, id);
  if (held.length > 0) {
    return { ...framesToClip(frames), pitches: held };
  }
  const smoothingActive = frames.some((f) => f.smoothedF0Hz != null);
  return {
    ...framesToClip(frames),
    pitches: frames
      .filter((f) => isRangeEvidence(f, smoothingActive))
      .map((f) => f.f0Hz as number),
  };
};

/**
 * 그 시도가 도달한 음역 끝(Hz). 엔진과 같은 게이트(4프레임 미만은 안 봄)를 써서
 * "시도는 했지만 잴 게 없는" 클립이 대표로 뽑히지 않게 한다.
 */
export const rangeHz = (clip: Clip, id: RangeSegmentId): number | null =>
  clip.pitches.length >= 4
    ? percentile(clip.pitches, RANGE_PERCENTILE[id])
    : null;

/**
 * `/a/` 길게의 시도 합치기 — **지표마다 규칙이 다르다**(티켓 12).
 *
 * MPT는 여러 시도 중 **최댓값**을 쓴다(임상 표준: 사람은 늘 자기 최대치를 못 낸다).
 * 반면 음정 정확도·안정성·비브라토는 최댓값이면 안 된다 — 여러 번 해서 제일 잘 나온
 * 걸 고르는 건 측정이 아니라 고르기다. 그래서 **마지막 시도**의 음정을 쓴다.
 * 결과적으로 voicedSec과 pitches가 서로 다른 시도에서 올 수 있다. 의도된 것이다:
 * 두 값은 같은 시도에서 나와야 할 이유가 없는 별개의 측정이다.
 */
const mergeSustain = (attempts: NativeF0Frame[][]): Clip => {
  const clips = attempts.map(framesToClip);
  const last = clips[clips.length - 1];
  return { ...last, voicedSec: Math.max(...clips.map((c) => c.voicedSec)) };
};

/**
 * 음역의 시도 합치기 — **가장 멀리 간 시도 하나를 통째로** 쓴다.
 * 시도들의 음정을 이어붙이지 않는 이유: 편한 음역대에 프레임이 몰리면 퍼센타일이
 * 그쪽으로 끌려와, 시도를 늘릴수록 음역이 좁아 보이는 역전이 생긴다.
 */
const bestRange = (
  attempts: NativeF0Frame[][],
  id: RangeSegmentId,
): Clip | undefined => {
  // 머문 음이 있는 시도가 하나라도 있으면 그 시도들끼리만 겨룬다 — 퍼센타일로 물러선 시도가
  // 머문 음보다 바깥 값을 내 대표를 빼앗지 않게.
  const held = attempts.filter((frames) => heldRangeF0(frames, id).length > 0);
  const clips = (held.length > 0 ? held : attempts).map((frames) =>
    rangeFramesToClip(frames, id),
  );
  const measured = clips.filter((c) => rangeHz(c, id) != null);
  if (measured.length === 0) {
    // 어느 시도도 잴 만큼 안 나왔다 — 마지막 시도를 그대로 넘겨 엔진이 판정하게 둔다.
    return clips[clips.length - 1];
  }
  const sign = id === 'low' ? -1 : 1;
  return measured.reduce((best, c) =>
    sign * (rangeHz(c, id) as number) > sign * (rangeHz(best, id) as number)
      ? c
      : best,
  );
};

/**
 * 저음·고음은 **시도가 있을 때만** 붙인다. 빈 clip을 넘기면 `buildReport`가
 * `pitches.length >= 4` 게이트에서 어차피 버리지만, 그때는 "안 불렀다"와 "불렀는데
 * 너무 짧다"가 구분되지 않는다 — 없는 건 없는 채로 넘긴다.
 */
const rangeClip = (
  attempts: NativeF0Frame[][] | undefined,
  id: RangeSegmentId,
): Clip | undefined =>
  attempts && attempts.length > 0 ? bestRange(attempts, id) : undefined;

export const framesToClips = (attempts: CaptureAttempts): ClipSet => ({
  sustain: attempts.sustain.length
    ? mergeSustain(attempts.sustain)
    : framesToClip([]),
  low: rangeClip(attempts.low, 'low'),
  high: rangeClip(attempts.high, 'high'),
});

/**
 * `/a/` 길게의 파생 스칼라 — **마지막 시도** 기준. mergeSustain이 음정을 뽑는 시도와
 * 같은 시도다: 시도들을 평균 내면 못한 시도가 잘한 시도를 깎고, 최댓값을 쓰면 측정이
 * 아니라 고르기가 된다(위 mergeSustain 주석과 같은 이유).
 */
export const sustainStats = (attempts: CaptureAttempts): FrameStats =>
  frameStats(attempts.sustain[attempts.sustain.length - 1] ?? []);

/**
 * 음역 구간별 안정성 — 이 회차에 **실제로 낸 모든 음**에서 잰다.
 *
 * 지속음 하나로는 성립하지 않는다: 그건 한 음이라 다섯 구간 중 하나에만 표본이 생긴다.
 * 그래서 네 세그먼트(지속음·저음·고음·노래)의 유성 프레임을 모은다 — 이번 회차에 낸 음은
 * 전부 "그 높이에서 목소리가 어땠나"의 증거다.
 *
 * **세그먼트마다 마지막 시도만** 쓴다(`mergeSustain`과 같은 규칙). 시도를 다 모으면
 * 삑사리가 나서 사용자가 그만두고 다시 부른 테이크의 불안정이 점수에 그대로 남는다.
 * 반대로 최고 시도만 고르면 측정이 아니라 고르기가 된다.
 *
 * 시간순으로 세워서 넘긴다. 엔진은 이웃한 두 표본의 **간격**으로 안정성을 재는데,
 * 세그먼트를 순서 없이 이어 붙이면 서로 다른 녹음의 두 프레임이 이웃이 되어 있지도 않은
 * 도약이 생긴다.
 *
 * **재개한 회차는 이번 세션의 프레임만 본다.** 복원한 진행에는 clip만 남고 프레임은 없다
 * (`DiagnosisProgress`). 그래서 앞 세션에서만 부른 구간은 표본이 모자라 점수가 없는 채로
 * 남는다 — 0으로 채우는 것보다 낫다("못 쟀다"가 사실이다).
 */
export const registerStats = (
  attempts: CaptureAttempts,
  range: RangeInfo | null,
): PitchRegister[] =>
  pitchRegisters(
    voicedSamples(
      Object.values(attempts).flatMap((tries) => tries[tries.length - 1] ?? []),
    ).sort((a, b) => a.tMs - b.tMs),
    range,
  );
